import { Link, useNavigate } from "react-router-dom";
import { Search, Bookmark, LogOut, ShieldCheck, Sparkles, Settings, Heart, Gift, LineChart } from "lucide-react";
import { useState } from "react";
import { useAuth } from "../lib/AuthContext";
import { Input } from "./ui/input";
import LocationPicker from "./LocationPicker";
import UserAvatar from "./UserAvatar";

export default function Navbar() {
  const { user, logout } = useAuth();
  const [q, setQ] = useState("");
  const navigate = useNavigate();

  const submit = (e) => {
    e.preventDefault();
    if (q.trim()) navigate(`/search?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-xl border-b border-black/10" data-testid="main-navbar">
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-6">
        <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
          <div className="w-8 h-8 bg-[#111111] flex items-center justify-center">
            <span className="text-white font-serif font-black text-xl leading-none">D</span>
          </div>
          <span className="font-serif font-black text-2xl tracking-tight">DealHub</span>
        </Link>

        <nav className="hidden md:flex items-center gap-5 text-sm font-medium">
          <Link to="/category/travel" className="hover:text-[#E63946]" data-testid="nav-travel">Travel</Link>
          <Link to="/category/clothing" className="hover:text-[#E63946]" data-testid="nav-clothing">Clothing</Link>
          <Link to="/category/auto" className="hover:text-[#E63946]" data-testid="nav-auto">Auto</Link>
          <Link to="/category/stocks" className="hover:text-[#E63946]" data-testid="nav-stocks">Stocks</Link>
          <Link to="/category/crypto" className="hover:text-[#E63946]" data-testid="nav-crypto">Crypto</Link>
          <Link to="/leaderboard" className="hover:text-[#E63946]" data-testid="nav-leaderboard">Board</Link>
          <Link to="/pricing" className="hover:text-[#E63946]" data-testid="nav-pricing">Pricing</Link>
          <Link to="/categories" className="hover:text-[#E63946]" data-testid="nav-all">All Categories</Link>
        </nav>

        <form onSubmit={submit} className="ml-auto flex items-center gap-2 flex-1 max-w-xs">
          <div className="relative w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-black/40" strokeWidth={1.5} />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search deals…"
              className="pl-9 rounded-none border-black/20 focus:border-black"
              data-testid="search-input"
            />
          </div>
        </form>

        <div className="flex items-center gap-2">
          <LocationPicker />
          {user ? (
            <>
              {user.is_pro && (
                <span className="hidden sm:inline-flex items-center gap-1 bg-emerald-600 text-white text-[10px] font-bold uppercase tracking-widest px-2 py-1" data-testid="nav-pro-badge" title="DealHub Pro">
                  ★ Pro
                </span>
              )}
              <Link to="/submit" className="hidden sm:inline-flex items-center gap-1.5 border border-black px-3 py-2 text-xs font-bold uppercase tracking-widest hover:bg-black hover:text-white" data-testid="nav-submit">
                <Sparkles className="w-3.5 h-3.5" strokeWidth={1.5} /> Submit
              </Link>
              <Link to="/bookmarks" className="p-2 hover:bg-black hover:text-white" data-testid="nav-bookmarks" title="Bookmarks">
                <Bookmark className="w-5 h-5" strokeWidth={1.5} />
              </Link>
              <Link to="/follows" className="p-2 hover:bg-black hover:text-white" data-testid="nav-follows" title="My Follows">
                <Heart className="w-5 h-5" strokeWidth={1.5} />
              </Link>
              <Link to="/referrals" className="p-2 hover:bg-black hover:text-white" data-testid="nav-referrals" title="Referral Rewards">
                <Gift className="w-5 h-5" strokeWidth={1.5} />
              </Link>
              <Link to="/curator" className="p-2 hover:bg-black hover:text-white" data-testid="nav-curator" title="Curator dashboard">
                <LineChart className="w-5 h-5" strokeWidth={1.5} />
              </Link>
              <Link to="/preferences" className="p-2 hover:bg-black hover:text-white" data-testid="nav-preferences" title="Preferences">
                <Settings className="w-5 h-5" strokeWidth={1.5} />
              </Link>
              {user.role === "admin" && (
                <Link to="/admin" className="p-2 hover:bg-black hover:text-white" data-testid="nav-admin" title="Admin">
                  <ShieldCheck className="w-5 h-5" strokeWidth={1.5} />
                </Link>
              )}
              <UserAvatar user={user} />
              <button onClick={logout} className="p-2 hover:bg-black hover:text-white" data-testid="logout-button" title="Logout">
                <LogOut className="w-5 h-5" strokeWidth={1.5} />
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="text-sm px-3 py-2 hover:underline" data-testid="nav-login">Log In</Link>
              <Link to="/register" className="text-sm px-4 py-2 bg-[#111111] text-white hover:bg-[#E63946]" data-testid="nav-register">Sign Up</Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
